import {
    useCreateCartItemMutation,
    useCreateSelfcareCartItemMutation,
    useDeleteCartItemByIdMutation,
    useDeleteCartItemsByIdMutation,
    useDeleteSelfcareCartItemByIdMutation,
    useDeleteSelfcareCartItemsByIdMutation,
} from 'store/api-slice/hooks'
import { BusTicketWithDetails } from 'store/api-slice/types'
import { useAppDispatch, useAppSelector } from 'store/hooks'
import { useApp } from 'store/steps'
import {
    updateRouteParams,
    removeRoute,
    removeTransport,
    setEditRoute,
} from '.'
import { useCart } from 'store/cart-slice/hooks'
import { useSelfcareCart } from 'store/selfcare-cart-slice/hooks'
import { sortDates } from 'helpers/helpers'
import { PromiseResponse } from 'helpers/types'
import {
    sumTicketsBundlesDates,
    sumTicketsDates,
} from 'components/global/Summary/helpers'
import { handleError, ErrorField } from 'helpers/errors'

type InitialData = {
    defaultDates?: string[]
    defaultQuantity?: number
}

export const useTransport = (
    routeIndex: number | null
): {
    addRouteToCart: () => Promise<PromiseResponse>
    editRouteInCart: () => Promise<PromiseResponse>
    removeRouteFromCart: () => Promise<PromiseResponse>
    removeTransportFromCart: () => Promise<PromiseResponse>
    getInitialData: () => InitialData
    isTransportProcessing: boolean
} => {
    const dispatch = useAppDispatch()
    const { language, isCreatorMode, isCreatorAccommodation } = useApp()

    const { routes, edited } = useAppSelector((state) => state.transport)
    const { busTickets: creatorCartBusTickets, accommodation } = useAppSelector(
        (state) => state.cart.items
    )
    const selfcareCartBusTickets = useAppSelector(
        (state) => state.selfcareCart.items.busTickets
    )

    const { tickets: cartParkTickets, ticketsBundles: cartTicketsBundles } =
        useAppSelector((state) => state.cart.items)
    const { people: parkTicketsQuantity } = useAppSelector(
        (state) => state.ticket
    )
    const {
        tickets: selfcareParkTickets,
        ticketsBundles: selfcareTicketsBundles,
        transport: selfcareBusTickets,
    } = useAppSelector((state) => state.selfcare)

    const { detail } = accommodation || {}
    const { dateFrom, dateTo } = detail || {}

    // CREATOR
    const { refetchCart: refetchCreatorCart, cartCreatorType } = useCart()
    const [postCreatorCartItem, postCreatorStatus] = useCreateCartItemMutation()
    const [deleteCreatorCartItem, deleteCreatorStatus] =
        useDeleteCartItemByIdMutation()
    const [deleteCreatorCartItems, deleteCreatorItemsStatus] =
        useDeleteCartItemsByIdMutation()

    // SELFCARE
    const { refetchSelfcareCart } = useSelfcareCart()
    const [postSelfcareCartItem, postSelfcareStatus] =
        useCreateSelfcareCartItemMutation()
    const [deleteSelfcareCartItem, deleteSelfcareStatus] =
        useDeleteSelfcareCartItemByIdMutation()
    const [deleteSelfcareCartItems, deleteSelfcareItemsStatus] =
        useDeleteSelfcareCartItemsByIdMutation()

    const cartBusTickets = isCreatorMode
        ? creatorCartBusTickets
        : selfcareCartBusTickets

    const refetchCart = isCreatorMode ? refetchCreatorCart : refetchSelfcareCart

    const isTransportProcessing = isCreatorMode
        ? postCreatorStatus.isLoading ||
          deleteCreatorStatus.isLoading ||
          deleteCreatorItemsStatus.isLoading
        : postSelfcareStatus.isLoading ||
          deleteSelfcareStatus.isLoading ||
          deleteSelfcareItemsStatus.isLoading

    const getCartFormattedTicket = (localTicket: BusTicketWithDetails) => {
        const isTicketOneDirection =
            localTicket.detail.routeType !== 'to_from_park'

        return {
            ...localTicket,
            detail: {
                ...localTicket.detail,
                returnDate: isTicketOneDirection
                    ? ''
                    : localTicket.detail.returnDate,
                returnTime: isTicketOneDirection
                    ? null
                    : localTicket.detail.returnTime,
            },
        }
    }

    const postTicket = async (ticket: BusTicketWithDetails) => {
        const { cartItemId, productId, productType, quantity, detail } = ticket

        if (!cartItemId || !productId || !productType || !quantity) return null

        const cartItemCommandData = {
            cartItemId,
            productId,
            productType,
            quantity,
            detail,
        }

        return isCreatorMode
            ? await postCreatorCartItem({
                  createCartItemCommand: { ...cartItemCommandData },
                  cartCreatorType,
                  _locale: language,
              })
            : await postSelfcareCartItem({
                  createCartItemSelfcareCommand: { ...cartItemCommandData },
                  _locale: language,
              })
    }

    const addRouteToCart = async () => {
        if (routeIndex === null) return { isSuccess: false }

        try {
            handleError(
                await postTicket(
                    getCartFormattedTicket(routes[routeIndex].ticket)
                )
            )
            dispatch(
                updateRouteParams({
                    index: routeIndex,
                    isSubmitted: true,
                })
            )
            refetchCart(true)
            return { isSuccess: true }
        } catch (errors: unknown) {
            const { messages } = errors as {
                messages: ErrorField[]
            }
            return { isSuccess: false, messages }
        }
    }

    const editRouteInCart = async () => {
        if (routeIndex === null || edited === null) return { isSuccess: false }

        const { ticket: originalTicket } = edited.origin
        const { ticket: modifiedTicket } = routes[routeIndex]

        if (!originalTicket.cartItemId) return { isSuccess: false }

        try {
            handleError(await handleDeleteCartItem(originalTicket.cartItemId))
        } catch (errors: unknown) {
            const { messages } = errors as {
                messages: ErrorField[]
            }
            dispatch(setEditRoute({ index: routeIndex, editState: 'editing' }))
            return { isSuccess: false, messages }
        }

        try {
            handleError(
                await postTicket(getCartFormattedTicket(modifiedTicket))
            )
        } catch (errors: unknown) {
            const { messages } = errors as {
                messages: ErrorField[]
            }
            dispatch(setEditRoute({ index: routeIndex, editState: 'declined' }))
            dispatch(removeRoute({ index: routeIndex }))
            refetchCart()
            return { isSuccess: false, messages }
        }

        dispatch(setEditRoute({ index: routeIndex, editState: 'completed' }))
        refetchCart()
        return { isSuccess: true }
    }

    const handleDeleteCartItem = async (cartItemId: string) =>
        isCreatorMode
            ? await deleteCreatorCartItem({
                  cartItemId,
                  _locale: language,
                  cartCreatorType,
              })
            : await deleteSelfcareCartItem({
                  cartItemId,
                  _locale: language,
              })

    const removeRouteFromCart = async () => {
        if (routeIndex === null) return { isSuccess: false }

        const { ticket } = routes[routeIndex]
        let currentCartItemId = ticket.cartItemId

        const isDeletingCurrentlyEdited = edited && routeIndex === edited.index
        if (isDeletingCurrentlyEdited)
            currentCartItemId = edited.origin.ticket.cartItemId

        if (!currentCartItemId) return { isSuccess: false }

        try {
            handleError(await handleDeleteCartItem(currentCartItemId))
            isDeletingCurrentlyEdited &&
                dispatch(
                    setEditRoute({ index: routeIndex, editState: 'completed' })
                )
            dispatch(removeRoute({ index: routeIndex }))
            refetchCart()
            return { isSuccess: true }
        } catch (errors: unknown) {
            const { messages } = errors as {
                messages: ErrorField[]
            }
            return { isSuccess: false, messages }
        }
    }

    const removeTransportFromCart = async () => {
        // in selfcare:
        // ignore already existed, but edited cabanas from cart
        // consider only newly added
        const editedSelfcareBusTickets =
            selfcareBusTickets?.map(
                (orderBusTicket) => orderBusTicket.changes?.detail.boxId
            ) || []

        const onlyNewCartTickets = cartBusTickets.filter(
            ({ detail: { boxId } }) =>
                boxId && !editedSelfcareBusTickets.includes(boxId)
        )

        const ticketsIdsToRemove = onlyNewCartTickets.map(
            (ticket) => ticket?.cartItemId || ''
        )

        if (!ticketsIdsToRemove.length) {
            dispatch(removeTransport())
            return { isSuccess: true }
        }

        try {
            const cartItemCommandData = {
                cartItemUuids: ticketsIdsToRemove,
            }
            const response = isCreatorMode
                ? await deleteCreatorCartItems({
                      _locale: language,
                      cartCreatorType,
                      deleteCartItemsByIdCommand: {
                          ...cartItemCommandData,
                      },
                  })
                : await deleteSelfcareCartItems({
                      _locale: language,
                      deleteCartItemsByIdSelfcareCommand: {
                          ...cartItemCommandData,
                      },
                  })

            handleError(response)
            dispatch(removeTransport())
            refetchCart()
            return { isSuccess: true }
        } catch (errors: unknown) {
            const { messages } = errors as {
                messages: ErrorField[]
            }
            return { isSuccess: false, messages }
        }
    }

    const getInitialData = () => {
        const selfcareParkTicketsDates = selfcareParkTickets
            ? selfcareParkTickets.map((ticket) => ticket.original.detail.day)
            : []
        const selfcareTicketsBundlesDates = selfcareTicketsBundles
            ? selfcareTicketsBundles.map(
                  (bundle) => bundle.original.detail.date
              )
            : []
        const selfcareParkDates = sortDates(
            Array.from(
                new Set([
                    ...selfcareParkTicketsDates,
                    ...selfcareTicketsBundlesDates,
                ])
            )
        )

        const selfcareParkTicketsQuantity = selfcareParkTicketsDates.filter(
            (date: string) => date === selfcareParkTicketsDates[0]
        ).length

        const isParkTicketInCart =
            cartParkTickets.length || cartTicketsBundles.length
        const isParkTicketInSelfcare =
            selfcareParkTickets?.length || selfcareTicketsBundles?.length

        if (isCreatorAccommodation && dateFrom && dateTo)
            return {
                defaultDates: [dateFrom, dateTo],
                defaultQuantity: parkTicketsQuantity,
            }
        else if (isParkTicketInCart) {
            const sumDates = cartParkTickets.length
                ? sumTicketsDates(cartParkTickets)
                : sumTicketsBundlesDates(cartTicketsBundles)

            return {
                defaultDates: sumDates.length
                    ? [sumDates[0], sumDates[sumDates.length - 1]]
                    : [],
                defaultQuantity: parkTicketsQuantity,
            }
        } else if (isParkTicketInSelfcare) {
            return {
                defaultDates: selfcareParkDates?.length
                    ? selfcareParkDates
                    : undefined,
                defaultQuantity: selfcareParkTicketsQuantity,
            }
        }

        return {}
    }

    return {
        addRouteToCart,
        editRouteInCart,
        removeRouteFromCart,
        removeTransportFromCart,
        isTransportProcessing,
        getInitialData,
    }
}
